/**
 * Client transport for the two Worker AI endpoints. Replaces ai-api-client.js:
 * the system and user prompts travel as `{ messages: [system, user] }` to
 * POST /api/ai/predict or /api/ai/analyze and the parsed JSON object comes
 * back (`{ ok: true, json }`), with a 30 s timeout. fetch is injected.
 */
export interface AiMessage {
  role: 'system' | 'user';
  content: string;
}

export type AiEndpoint = 'predict' | 'analyze';

export interface AiTransport {
  /** Resolves with the model's JSON object; rejects on timeout, HTTP failure or `{ ok: false }`. */
  call(endpoint: AiEndpoint, messages: [AiMessage, AiMessage]): Promise<unknown>;
}

export const AI_CLIENT_TIMEOUT_MS = 30_000;

export function buildAiMessages(systemPrompt: string, userPrompt: string): [AiMessage, AiMessage] {
  return [
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ];
}

export interface AiTransportDeps {
  fetch: (input: string, init: RequestInit) => Promise<Response>;
  timeoutMs?: number;
  /** Prefix for the endpoint paths; empty for same-origin. */
  baseUrl?: string;
}

export function createAiTransport(deps: AiTransportDeps): AiTransport {
  const timeoutMs = deps.timeoutMs ?? AI_CLIENT_TIMEOUT_MS;
  const baseUrl = deps.baseUrl ?? '';
  return {
    async call(endpoint, messages) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await deps.fetch(baseUrl + '/api/ai/' + endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages }),
          signal: controller.signal,
        });
        const body = (await response.json()) as { ok?: boolean; json?: unknown; error?: string };
        if (!response.ok || !body || body.ok !== true) {
          throw new Error(body && body.error ? body.error : 'AI request failed with HTTP ' + response.status);
        }
        return body.json;
      } finally {
        clearTimeout(timer);
      }
    },
  };
}
